/**
 * Cascade Phase Timing Utility
 *
 * Computes DB update payloads when actual phase timing is reported.
 * Only updates started_at / duration (actual columns). est_ fields are untouched.
 *
 * All boundaries are expressed as a single date (the boundary point):
 *   'start'      → boundaryDate = when phase[0] starts
 *   number N     → boundaryDate = when phase[N] ends / phase[N+1] starts
 *                  left duration is computed: boundaryDate − phase[N].est_started_at
 *   'end'        → boundaryDate = when the last phase ends
 *                  last duration is computed: boundaryDate − lastPhase.est_started_at
 */

import { parseIntervalToDays } from '~/common/utils/date-utils'

/**
 * Get the effective duration in days for a week-format phase.
 * Uses the merged est_duration string (which already reflects actual if set).
 */
function getPhaseDurationDays(phase) {
  return parseIntervalToDays(phase.est_duration) || 1
}

/**
 * Build cascade update payloads for actual phase timing.
 *
 * @param {Array}  phases        - Week-format phase objects (sorted by step asc)
 *                                 Each has: phaseId, est_started_at, est_duration, step
 * @param {string} boundaryType  - 'start' | 'end' | number (0-based intersection index)
 * @param {string} boundaryDate  - ISO date string for the boundary point
 *
 * @returns {Array} Update payloads: [{ phaseId, started_at?, duration? }, ...]
 *   `duration` values use PostgreSQL interval format "N days".
 */
export function cascadePhaseTimingUpdate(phases, boundaryType, boundaryDate) {
  if (!phases || phases.length === 0 || !boundaryDate) return []

  const boundary = new Date(boundaryDate)
  const updates = []

  if (boundaryType === 'start') {
    // Set started_at of first phase, cascade all subsequent phases
    let cascadeDate = new Date(boundary)
    phases.forEach(phase => {
      updates.push({ phaseId: phase.phaseId, started_at: cascadeDate.toISOString() })
      const durationDays = getPhaseDurationDays(phase)
      cascadeDate = new Date(cascadeDate)
      cascadeDate.setDate(cascadeDate.getDate() + durationDays)
    })
    return updates
  }

  if (boundaryType === 'end') {
    // Compute duration for last phase: boundaryDate − lastPhase.est_started_at
    const lastPhase = phases[phases.length - 1]
    const lastStart = new Date(lastPhase.est_started_at)
    const durationDays = Math.max(1, Math.round((boundary - lastStart) / (24 * 60 * 60 * 1000)))
    updates.push({ phaseId: lastPhase.phaseId, duration: `${durationDays} days` })
    return updates
  }

  // Intersection N: boundaryType is 0-based index of the left phase
  const intersectionIndex = boundaryType
  const leftPhase = phases[intersectionIndex]
  const rightPhase = phases[intersectionIndex + 1]
  if (!leftPhase || !rightPhase) return []

  // Compute left phase duration: boundaryDate − leftPhase.est_started_at
  const leftStart = new Date(leftPhase.est_started_at)
  const leftDurationDays = Math.max(1, Math.round((boundary - leftStart) / (24 * 60 * 60 * 1000)))
  updates.push({ phaseId: leftPhase.phaseId, duration: `${leftDurationDays} days` })

  // Set right phase started_at = boundary, cascade all subsequent phases
  let cascadeDate = new Date(boundary)
  for (let i = intersectionIndex + 1; i < phases.length; i++) {
    const phase = phases[i]
    updates.push({ phaseId: phase.phaseId, started_at: cascadeDate.toISOString() })
    const durationDays = getPhaseDurationDays(phase)
    cascadeDate = new Date(cascadeDate)
    cascadeDate.setDate(cascadeDate.getDate() + durationDays)
  }

  return updates
}

/**
 * Merge a set of update payloads into a single record per phaseId.
 * When both started_at and duration are set for the same phase, they are combined.
 */
export function mergePhaseUpdates(updates) {
  const map = new Map()
  for (const update of updates) {
    const existing = map.get(update.phaseId) || { phaseId: update.phaseId }
    map.set(update.phaseId, { ...existing, ...update })
  }
  return [...map.values()]
}
